import { ChevronRight, LoaderCircle, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Bank } from "@/apis/gql/types";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useCallback, useState } from "react";
import { getBankInitials } from "../utils";

interface BankCardProps {
  bank: Bank;
  nonSelectView?: boolean;
  onSelect: () => void;
  disabled?: boolean;
}

export default function BankCard({
  bank,
  onSelect,
  disabled = false,
  nonSelectView = false,
}: BankCardProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const isPisSupported = bank.interfaces.some((i) => i.isPisSupported);

  const supportsInstantTransfer = bank.interfaces.some(
    (i) => i.paymentCapabilities?.sepaInstantMoneyTransfer,
  );

  const supportsCollectiveTransfer = bank.interfaces.some(
    (i) => i.paymentCapabilities?.sepaCollectiveMoneyTransfer,
  );

  const health = Math.max(...bank.interfaces.map((i) => i.health || 0));
  let healthStatus = "critical";
  if (health >= 90) healthStatus = "excellent";
  else if (health >= 70) healthStatus = "good";
  else if (health >= 50) healthStatus = "moderate";

  const healthColors = {
    excellent: "bg-emerald-500",
    good: "bg-green-500",
    moderate: "bg-amber-500",
    critical: "bg-red-500",
  };

  const handleSelect = useCallback(() => {
    onSelect();
  }, [onSelect]);

  return (
    <button
      onClick={handleSelect}
      className="w-full text-left group disabled:opacity-50 disabled:cursor-not-allowed"
      type="button"
      disabled={isLoading || disabled}
    >
      <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="relative flex-shrink-0">
            <div className="h-12 w-12 rounded-lg bg-white p-2 flex items-center justify-center overflow-hidden border border-gray-200">
              {bank.icon?.url ? (
                <img
                  src={bank.icon?.url}
                  alt={bank.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gray-100 text-gray-600 font-medium">
                  {getBankInitials(bank.name)}
                </div>
              )}
            </div>
            {/* TODO: Add health status */}
            {/* <div
              className={cn(
                "absolute -top-1 -right-1 h-4 w-4 rounded-full border-2 border-white",
                healthColors[healthStatus as keyof typeof healthColors],
              )}
            /> */}
          </div>
          <div>
            <h3 className="font-medium text-gray-900 group-hover:text-gray-900">
              {bank.name}
            </h3>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span className="flex items-center">
                <MapPin className="h-3 w-3 mr-0.5" />
                {bank.city}, {bank.location}
              </span>
              <span className="h-1 w-1 rounded-full bg-gray-300" />
              <span>{bank.blz}</span>
              <span className="h-1 w-1 rounded-full bg-gray-300" />
              <span className="font-mono">{bank.bic}</span>
            </div>
            {/* TODO: Add payment capabilities */}
            {/* <div className="mt-1 flex flex-wrap gap-1">
              {isPisSupported && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {t("bank_connections.connection_step_zero.payments")}
                </span>
              )}
              {supportsInstantTransfer && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {t("bank_connections.connection_step_zero.instant")}
                </span>
              )}
              {supportsCollectiveTransfer && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                  {t("bank_connections.connection_step_zero.collective")}
                </span>
              )}
            </div> */}
          </div>
        </div>
        {isLoading ? (
          <LoaderCircle className="h-5 w-5 text-gray-400 animate-spin" />
        ) : nonSelectView ? null : (
          <ChevronRight
            className={cn(
              "h-5 w-5 text-gray-400 transition-transform duration-200",
              "group-hover:text-gray-600 group-hover:translate-x-1",
            )}
          />
        )}
      </div>
    </button>
  );
}

export const BankCardSkeleton = () => {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          <div className="relative flex-shrink-0">
            <Skeleton className="h-12 w-12 rounded-lg" />
          </div>
          <div>
            <Skeleton className="h-5 w-32 mb-2" />
            <div className="flex items-center space-x-2">
              <Skeleton className="h-3 w-20" />
              <span className="h-1 w-1 rounded-full bg-gray-300" />
              <Skeleton className="h-3 w-16" />
              <span className="h-1 w-1 rounded-full bg-gray-300" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        </div>
        <Skeleton className="h-5 w-5" />
      </div>
    </div>
  );
};
